import { useAppContext } from '../../../context/AppContext';
import { fetchData } from '../../../services/fetchData';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CompanyDetails } from '../../blocks/CompanyDetails';
import { ProfileSettings } from '../../blocks/ProfileSettings';
import { UsersTable } from '../../tables/UsersTable';
import { AddUserForm } from '../../forms';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
const TABS = {
    company: 'company',
    users: 'users',
    profile: 'profile',
};

function resolveActiveTab(tabParam, isCompanyAdmin) {
    if (tabParam === TABS.profile) return TABS.profile;
    if (tabParam === TABS.users && isCompanyAdmin) return TABS.users;
    return TABS.company;
}

export const AdminPage = () => {
    const { token, base_url, user, company, isLoadingCompany } = useAppContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const isCompanyAdmin = user?.is_admin === true;

    const activeTab = resolveActiveTab(searchParams.get('tab'), isCompanyAdmin);

    const [users, setUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [showAddUserForm, setShowAddUserForm] = useState(false);

    useEffect(() => {
        if (!isCompanyAdmin) return;

        const loadUsers = async () => {
            if (!token) return;
            try {
                setIsLoadingUsers(true);
                const response = await fetchData(token, `${base_url}/apis/admin/users`);
                if (response && response.users) {
                    setUsers(response.users);
                } else {
                    window.showAlert(500, 'Failed to load users');
                }
            } catch (err) {
                window.showAlert(err.status || 500, err?.msg || 'Error loading users');
                console.error('Error loading users:', err);
            } finally {
                setIsLoadingUsers(false);
            }
        };

        loadUsers();
    }, [token, base_url, isCompanyAdmin]);

    const setActiveTab = (tab) => {
        if (tab === TABS.company) {
            setSearchParams({}, { replace: true });
        } else {
            setSearchParams({ tab }, { replace: true });
        }
    };

    return (
        <div className="main-content">
            <h1 className="admin-page-title">
                {isCompanyAdmin ? 'Admin' : 'Company'}
            </h1>

            <div className="admin-tabs">
                <div className="admin-tabs__list">
                    <button
                        type="button"
                        onClick={() => setActiveTab(TABS.company)}
                        className={`admin-tab${activeTab === TABS.company ? ' admin-tab--active' : ''}`}
                    >
                        Company
                    </button>
                    {isCompanyAdmin && (
                        <button
                            type="button"
                            onClick={() => setActiveTab(TABS.users)}
                            className={`admin-tab${activeTab === TABS.users ? ' admin-tab--active' : ''}`}
                        >
                            Users
                            <span className={`admin-tab__count${activeTab === TABS.users ? ' admin-tab__count--active' : ''}`}>
                                {users.length}
                            </span>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setActiveTab(TABS.profile)}
                        className={`admin-tab${activeTab === TABS.profile ? ' admin-tab--active' : ''}`}
                    >
                        My Profile
                    </button>
                </div>
            </div>

            {activeTab === TABS.company && (
                <>
                    {isLoadingCompany ? (
                        <div className="company-container">
                            <div className="loading-panel loading-state">
                                <div className="loading-spinner"></div>
                                <p>Loading company information...</p>
                            </div>
                        </div>
                    ) : (
                        <CompanyDetails company={company} showEditButton={true} />
                    )}
                </>
            )}

            {activeTab === TABS.users && isCompanyAdmin && (
                <div>
                    <div className="admin-section-header">
                        <h2>Manage Users</h2>
                        <div className="admin-section-header__meta">
                            <div className="admin-section-header__count">
                                {users.length} user{users.length !== 1 ? 's' : ''} total
                            </div>
                            <button
                                className="btn btn-primary admin-btn-add"
                                onClick={() => setShowAddUserForm(true)}
                            >
                                <FontAwesomeIcon icon={faPlus} className="icon" />
                                Add User
                            </button>
                        </div>
                    </div>

                    <UsersTable users={users} setUsers={setUsers} isLoading={isLoadingUsers} />

                    {showAddUserForm && (
                        <AddUserForm
                            setUsers={setUsers}
                            onClose={() => setShowAddUserForm(false)}
                        />
                    )}
                </div>
            )}

            {activeTab === TABS.profile && <ProfileSettings />}
        </div>
    );
};
